import { useState } from 'react';

type UseListKeyboardNavigationProps = {
  /** リスト項目数 */
  itemCount: number;
  /** Enter 押下時のコールバック（アクティブ項目のインデックスを受け取る） */
  onSelect: (index: number) => void;
  /** Escape 押下時のコールバック */
  onClose: () => void;
  /** キーボードナビゲーションを有効にするかどうか（デフォルト: true） */
  enabled?: boolean;
};

/**
 * リストのキーボードナビゲーション共通フック
 *
 * ArrowDown/ArrowUp でのインデックス循環移動、Enter による選択、Escape による閉じるを提供する。
 * Autocomplete / Select / Cascader の検索リストで共通利用。
 *
 * - Enter / Escape 後は activeIndex を自動リセット（-1）
 * - enabled=false 時はキー入力を無視
 * - 検索値変更・Popover 閉じ時など外部からのリセットは resetActiveIndex() を使用
 */
export const useListKeyboardNavigation = ({
  itemCount,
  onSelect,
  onClose,
  enabled = true,
}: UseListKeyboardNavigationProps) => {
  const [activeIndex, setActiveIndex] = useState(-1);

  const resetActiveIndex = () => setActiveIndex(-1);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!enabled || itemCount === 0) return;

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        setActiveIndex((prev) => (prev < itemCount - 1 ? prev + 1 : 0));
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : itemCount - 1));
        break;
      }
      case 'Enter': {
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < itemCount) {
          onSelect(activeIndex);
          setActiveIndex(-1);
        }
        break;
      }
      case 'Escape': {
        e.preventDefault();
        onClose();
        setActiveIndex(-1);
        break;
      }
    }
  };

  return { activeIndex, handleKeyDown, resetActiveIndex };
};
